import React, { useState, useRef } from 'react';
import { 
  User, Save, Target, AlertTriangle, Dumbbell, 
  ChevronRight, Check, Settings as SettingsIcon, Camera
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import DashboardLayout from '../components/DashboardLayout';
import PageHeader from '../components/PageHeader';
import NikkiMemoryCard from '../components/NikkiMemoryCard';
import WellnessAvatarCard from '../components/WellnessAvatarCard';
import VillageOptinCard from '../components/VillageOptinCard';
import NutritionBaselineSection from '../components/NutritionBaselineSection';
import { useAuth } from '../context/AuthContext';
import { avatarUrl } from '../utils/avatar';
import { AvatarCropDialog } from '../components/AvatarCropDialog';
import { toast } from 'sonner';

const Profile = () => {
  const { user, token, updateProfile, uploadAvatar } = useAuth();
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [cropSrc, setCropSrc] = useState(null);
  const fileRef = useRef(null);
  const photo = avatarUrl(user, token);

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/.test(file.type)) {
      toast.error('Please choose a PNG, JPG or WebP image.');
      return;
    }
    if (file.size > 25 * 1024 * 1024) {
      toast.error('That image is huge — pick one under 25MB.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCropSrc(reader.result);
    reader.readAsDataURL(file);
  };

  const handleCropSave = async (blob) => {
    if (!blob) return;
    setUploading(true);
    try {
      const cropped = new File([blob], 'avatar.jpg', { type: 'image/jpeg' });
      await uploadAvatar(cropped);
      toast.success('Profile photo updated');
      setCropSrc(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Upload failed — try again.');
    } finally {
      setUploading(false);
    }
  };
  
  const [formData, setFormData] = useState({
    goals: user?.goals || [],
    fitness_level: user?.fitness_level || 'intermediate',
    past_injuries: user?.past_injuries || [],
    mobility_limitations: user?.mobility_limitations || [],
    available_equipment: user?.available_equipment || []
  });

  const [newGoal, setNewGoal] = useState('');
  const [newInjury, setNewInjury] = useState('');
  const [newLimitation, setNewLimitation] = useState('');
  const [newEquipment, setNewEquipment] = useState('');

  const goalOptions = ['Build Muscle', 'Lose Fat', 'Increase Strength', 'Improve Endurance', 'Better Mobility', 'Sports Performance', 'General Fitness'];
  const equipmentOptions = ['Barbell', 'Dumbbells', 'Kettlebells', 'Pull-up Bar', 'Resistance Bands', 'Cable Machine', 'Squat Rack', 'Bench', 'Treadmill', 'Rowing Machine'];

  const addItem = (field, value, setValue) => {
    if (value && !formData[field].includes(value)) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value]
      }));
      setValue('');
    }
  };

  const removeItem = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter(item => item !== value)
    }));
  };

  const handleSave = async () => {
    setLoading(true);
    try {
      await updateProfile(formData);
      toast.success('Profile updated successfully');
    } catch (error) {
      toast.error('Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const TagList = ({ items, field, color = 'primary' }) => (
    <div className="flex flex-wrap gap-2">
      {items.map((item, idx) => (
        <span 
          key={idx}
          className={`px-3 py-1 rounded-sm text-sm flex items-center gap-2 ${
            color === 'primary' ? 'bg-primary/20 text-primary' :
            color === 'red' ? 'bg-red-500/20 text-red-400' :
            color === 'yellow' ? 'bg-yellow-500/20 text-yellow-500' :
            'bg-accent-blue/20 text-accent-blue'
          }`}
        >
          {item}
          <button 
            onClick={() => removeItem(field, item)}
            className="hover:opacity-70"
          >
            ×
          </button>
        </span>
      ))}
    </div>
  );

  return (
    <DashboardLayout>
      {cropSrc && (
        <AvatarCropDialog
          imageSrc={cropSrc}
          saving={uploading}
          onCancel={() => setCropSrc(null)}
          onSave={handleCropSave}
        />
      )}
      <div className="p-6 space-y-6 max-w-4xl">
        {/* Header */}
        <PageHeader
          tag="Profile"
          tagColor="#CCFF00"
          icon={SettingsIcon}
          title="Profile settings"
          subtitle="Customize your training preferences"
          testIdPrefix="profile"
          actions={(
            <Button 
              className="hpme-btn-primary flex items-center gap-2"
              onClick={handleSave}
              disabled={loading}
              data-testid="save-profile-btn"
            >
              <Save className="w-5 h-5" />
              {loading ? 'Saving...' : 'Save Changes'}
            </Button>
          )}
        />

        {/* User Info */}
        <div className="hpme-card p-6">
          <div className="hpme-card-corner" />
          <div className="flex items-center gap-4 mb-6">
            <div className="relative group shrink-0">
              <div className="w-16 h-16 rounded-full bg-zinc-800 overflow-hidden flex items-center justify-center">
                {photo
                  ? <img src={photo} alt="Profile" className="w-full h-full object-cover" data-testid="profile-avatar-img" />
                  : <User className="w-8 h-8 text-zinc-400" />}
              </div>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-primary text-black flex items-center justify-center shadow-md hover:opacity-90 disabled:opacity-50"
                data-testid="profile-avatar-upload-btn"
                aria-label="Upload profile photo"
              >
                {uploading ? <span className="w-3 h-3 border-2 border-black/40 border-t-black rounded-full animate-spin" /> : <Camera className="w-4 h-4" />}
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                className="hidden"
                onChange={handleAvatarChange}
                data-testid="profile-avatar-input"
              />
            </div>
            <div>
              <h2 className="font-secondary font-bold text-2xl">{user?.name}</h2>
              <p className="text-zinc-400">{user?.email}</p>
              <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs uppercase rounded-sm">
                {user?.role}
              </span>
            </div>
          </div>
        </div>

        {/* Nikki's memory — what she remembers (and the user can forget) */}
        <NikkiMemoryCard />

        {/* Wellness Avatar — import an existing set (free) or regenerate */}
        <WellnessAvatarCard />

        {/* Village privacy — appear as avatar+name, or stay anonymous */}
        <VillageOptinCard />

        {/* Nutrition baseline — single source of truth for kcal/protein targets */}
        <NutritionBaselineSection />

        {/* Fitness Level */}
        <div className="hpme-card p-6">
          <div className="hpme-card-corner" />
          <h3 className="font-secondary font-bold text-xl mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            FITNESS LEVEL
          </h3>
          <Select 
            value={formData.fitness_level} 
            onValueChange={(v) => setFormData(p => ({ ...p, fitness_level: v }))}
          >
            <SelectTrigger className="hpme-input max-w-xs" data-testid="fitness-level-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-zinc-900 border-zinc-800">
              <SelectItem value="beginner">Beginner - New to training</SelectItem>
              <SelectItem value="intermediate">Intermediate - 1-3 years experience</SelectItem>
              <SelectItem value="advanced">Advanced - 3+ years experience</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Goals */}
        <div className="hpme-card p-6">
          <div className="hpme-card-corner" />
          <h3 className="font-secondary font-bold text-xl mb-4 flex items-center gap-2">
            <Target className="w-5 h-5 text-primary" />
            TRAINING GOALS
          </h3>
          
          <TagList items={formData.goals} field="goals" color="primary" />
          
          <div className="flex gap-2 mt-4">
            <Select value={newGoal} onValueChange={setNewGoal}>
              <SelectTrigger className="hpme-input flex-1" data-testid="goal-select">
                <SelectValue placeholder="Select a goal" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {goalOptions.filter(g => !formData.goals.includes(g)).map(goal => (
                  <SelectItem key={goal} value={goal}>{goal}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              className="hpme-btn-secondary"
              onClick={() => addItem('goals', newGoal, setNewGoal)}
              data-testid="add-goal-btn"
            >
              Add
            </Button>
          </div>
        </div>

        {/* Past Injuries */}
        <div className="hpme-card p-6">
          <div className="hpme-card-corner" />
          <h3 className="font-secondary font-bold text-xl mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-red-400" />
            PAST INJURIES
          </h3>
          <p className="text-zinc-400 text-sm mb-4">
            List any past injuries so AI can adapt workouts accordingly
          </p>
          
          <TagList items={formData.past_injuries} field="past_injuries" color="red" />
          
          <div className="flex gap-2 mt-4">
            <Input
              className="hpme-input flex-1"
              placeholder="e.g., ACL tear, shoulder impingement"
              value={newInjury}
              onChange={(e) => setNewInjury(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addItem('past_injuries', newInjury, setNewInjury)}
              data-testid="injury-input"
            />
            <Button 
              className="hpme-btn-secondary"
              onClick={() => addItem('past_injuries', newInjury, setNewInjury)}
              data-testid="add-injury-btn"
            >
              Add
            </Button>
          </div>
        </div>

        {/* Mobility Limitations */}
        <div className="hpme-card p-6">
          <div className="hpme-card-corner" />
          <h3 className="font-secondary font-bold text-xl mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-yellow-500" />
            MOBILITY LIMITATIONS
          </h3>
          <p className="text-zinc-400 text-sm mb-4">
            Any mobility restrictions that affect your training
          </p>
          
          <TagList items={formData.mobility_limitations} field="mobility_limitations" color="yellow" />
          
          <div className="flex gap-2 mt-4">
            <Input
              className="hpme-input flex-1"
              placeholder="e.g., limited ankle dorsiflexion"
              value={newLimitation}
              onChange={(e) => setNewLimitation(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && addItem('mobility_limitations', newLimitation, setNewLimitation)}
              data-testid="limitation-input"
            />
            <Button 
              className="hpme-btn-secondary"
              onClick={() => addItem('mobility_limitations', newLimitation, setNewLimitation)}
              data-testid="add-limitation-btn"
            >
              Add
            </Button>
          </div>
        </div>

        {/* Available Equipment */}
        <div className="hpme-card p-6">
          <div className="hpme-card-corner" />
          <h3 className="font-secondary font-bold text-xl mb-4 flex items-center gap-2">
            <Dumbbell className="w-5 h-5 text-accent-blue" />
            AVAILABLE EQUIPMENT
          </h3>
          <p className="text-zinc-400 text-sm mb-4">
            What equipment do you have access to?
          </p>
          
          <TagList items={formData.available_equipment} field="available_equipment" color="blue" />
          
          <div className="flex gap-2 mt-4">
            <Select value={newEquipment} onValueChange={setNewEquipment}>
              <SelectTrigger className="hpme-input flex-1" data-testid="equipment-select">
                <SelectValue placeholder="Select equipment" />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {equipmentOptions.filter(e => !formData.available_equipment.includes(e)).map(eq => (
                  <SelectItem key={eq} value={eq}>{eq}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button 
              className="hpme-btn-secondary"
              onClick={() => addItem('available_equipment', newEquipment, setNewEquipment)}
              data-testid="add-equipment-btn"
            >
              Add
            </Button>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Profile;
